/**
 * RevenueCat StoreKit wrapper — iOS native only.
 * The public SDK key is safe to ship in the binary by design.
 */
import { getPlatform } from "@/lib/nativeBridge";

const RC_IOS_KEY = process.env.REACT_APP_RC_IOS_KEY || "appl_TQjwlvPoFStsscqEbdoQABLSWRD";

let _purchases = null;
let _configuredFor = null;

export function iapSupported() {
  return getPlatform() === "ios" && Boolean(RC_IOS_KEY);
}

async function sdk() {
  if (!_purchases) {
    const mod = await import("@revenuecat/purchases-capacitor");
    _purchases = mod.Purchases;
  }
  // Wrap: the Capacitor plugin proxy intercepts `.then`, so it must
  // never be a promise's direct resolution value.
  return { P: _purchases };
}

export async function ensureConfigured(userId) {
  if (!iapSupported() || !userId) return null;
  const { P } = await sdk();
  if (_configuredFor !== userId) {
    await P.configure({ apiKey: RC_IOS_KEY, appUserID: userId });
    _configuredFor = userId;
  }
  return { P };
}

export async function getIapPackages(userId) {
  const wrapped = await ensureConfigured(userId);
  if (!wrapped) return [];
  const res = await wrapped.P.getOfferings();
  return res?.current?.availablePackages || [];
}

export async function purchaseIapPackage(userId, pkg) {
  const wrapped = await ensureConfigured(userId);
  const result = await wrapped.P.purchasePackage({ aPackage: pkg });
  return result?.customerInfo || null;
}

// Consumable products (one-time, repeatable) — fetched by product id,
// not via offerings. Credits are granted server-side by the RC webhook.
export const IAP_PRODUCTS = {
  dog_registration: "ak9.registration.dog",
  litter_registration: "ak9.registration.litter",
  certificate: "ak9.certificate",
};

export async function getIapProduct(userId, productId) {
  const wrapped = await ensureConfigured(userId);
  if (!wrapped) return null;
  const res = await wrapped.P.getProducts({ productIdentifiers: [productId] });
  return (res?.products || [])[0] || null;
}

export async function purchaseIapProduct(userId, product) {
  const wrapped = await ensureConfigured(userId);
  const result = await wrapped.P.purchaseStoreProduct({ product });
  return result?.customerInfo || null;
}

export async function restoreIapPurchases(userId) {
  const wrapped = await ensureConfigured(userId);
  const result = await wrapped.P.restorePurchases();
  return result?.customerInfo || null;
}
